import React, { useEffect, useState, useCallback, memo, useRef } from 'react';
import {
  Box,
  Icon,
  Typography,
  Link,
  Button,
  useMediaQuery,
  useTheme,
  IconButton,
  Menu,
  MenuItem,
} from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import moment from 'moment';

import Loading from './Loading';
import useStyles from './styles';

const getTypeIcon = (type, status) => {
  if (type !== 'note') {
    switch (status) {
      // pending
      case 'PENDING':
        return 'sms';
      // rejected
      case 'REJECTED':
        return 'sms_failed';
      // accepted
      case 'APPROVED':
      default:
        return 'chat_bubble_outline';
    }
  }
  return 'subject';
};

const Comment = props => {
  const classes = useStyles(props);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const commentRef = React.useRef(null);

  const {
    status,
    title,
    msg,
    date,
    responses,
    toggleResponses,
    toggleReply,
    type,
    isLoading,
    className = '',
    url = null,
    actions,
    editComment,
    isReply,
  } = props;

  const [msgCrop, setMsgCrop] = useState(false);
  const [openActionsMenu, setOpenActionsMenu] = useState(false);
  const [anchorElActions, setAnchorElActions] = React.useState(null);
  const [msgCanBeCrop, setMsgCanBeCrop] = useState(false);
  const [formatedDate, setFormatedDate] = useState(false);
  const [focusComment, setFocusComment] = useState(false);
  const msgRef = useRef(null);

  const handleClickView = useCallback(() => {
    setMsgCrop(oldMsgCrop => !oldMsgCrop);
  }, []);

  const handleOpenActions = useCallback(event => {
    setOpenActionsMenu(true);
    setAnchorElActions(event.currentTarget);
  }, []);

  const handleCloseActions = useCallback(() => {
    setOpenActionsMenu(false);
  }, []);

  const handleAction = useCallback(
    callback => {
      if (callback()) {
        callback()();
      }
      handleCloseActions();
    },
    [handleCloseActions],
  );

  const createMarkup = useCallback(
    () => ({
      // eslint-disable-next-line no-control-regex
      __html: msg.replace(new RegExp('\n', 'g'), '<br />'),
    }),
    [msg],
  );

  const changeFocusComment = useCallback(() => {
    setFocusComment(oldState => !oldState);
  }, []);

  const changeBlurComment = useCallback(
    e => {
      if (!commentRef?.current?.contains(e.target)) {
        setFocusComment(false);
      }
    },
    [commentRef],
  );

  const checkMsgCanBeCrop = useCallback(() => {
    const height = msgRef?.current?.offsetHeight ?? 0;
    if (height > 60) {
      setMsgCanBeCrop(true);
      setMsgCrop(true);
    } else {
      setMsgCanBeCrop(false);
    }
  }, [msgRef]);

  useEffect(() => {
    document.addEventListener('mousedown', changeBlurComment);
    return () => document.removeEventListener('mousedown', changeBlurComment);
  }, [changeBlurComment]);

  useEffect(() => {
    if (date) {
      setFormatedDate(moment(date).format('DD/MM/YYYY - HH[h]mm[m]'));
    }
  }, [date]);

  useEffect(() => {
    checkMsgCanBeCrop();
    window.addEventListener('resize', checkMsgCanBeCrop);
    return () => {
      window.removeEventListener('resize', checkMsgCanBeCrop);
    };
  }, [checkMsgCanBeCrop, msg]);

  return isLoading ? (
    <Loading className={className} />
  ) : (
    <Box
      className={`${classes.container} ${className} ${
        focusComment ? 'is-focused' : ''
      }`}
      onClick={changeFocusComment}
      ref={commentRef}
    >
      <Box className="comment-avatar">
        <Icon className="comment-avatar-icon" fontSize="small">
          {getTypeIcon(type, status)}
        </Icon>
      </Box>
      <Box className="comment-content">
        <Box className="comment-ballon">
          {title && url ? (
            <Link
              className="comment-ballon-title"
              variant="caption"
              to={url}
              component={url ? RouterLink : Typography}
            >
              {title}
            </Link>
          ) : (
            <Typography
              className="comment-ballon-title not-link"
              variant="caption"
            >
              {title}
            </Typography>
          )}

          {isSmallScreen && formatedDate ? (
            <Typography className="comment-ballon-date" variant="caption">
              {formatedDate}
            </Typography>
          ) : null}

          {editComment ? (
            <editComment.ComponentInput {...editComment.componentProps} />
          ) : (
            <>
              <Typography
                className={`comment-ballon-msg ${msgCrop ? 'cropped' : null}`}
                variant="body2"
              >
                <>
                  {/* eslint-disable-next-line react/no-danger */}
                  <span ref={msgRef} dangerouslySetInnerHTML={createMarkup()} />
                </>
              </Typography>
              {msgCanBeCrop ? (
                <Link
                  className="comment-ballon-view-more"
                  component="button"
                  onClick={handleClickView}
                >
                  {`Ver ${msgCrop ? 'mais' : 'menos'}`}
                </Link>
              ) : null}
              {actions ? (
                <Box className="comment-actions-container">
                  <IconButton
                    className="comment-actions-button"
                    onClick={handleOpenActions}
                  >
                    <Icon
                      className="comment-actions-button-icon"
                      fontSize="small"
                    >
                      more_vert
                    </Icon>
                  </IconButton>
                  <Menu
                    open={openActionsMenu}
                    anchorEl={anchorElActions}
                    onClose={handleCloseActions}
                    variant="menu"
                  >
                    {actions.map(action => (
                      <MenuItem
                        key={action.name}
                        onClick={() => handleAction(action.cb)}
                      >
                        <Typography variant="body2" color="textSecondary">
                          {action.name}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              ) : null}
            </>
          )}
        </Box>

        {type !== 'note' ? (
          <Box className="comment-footer">
            <Box className="comment-footer-btn-container">
              {status === 'APPROVED' && toggleReply && !isReply ? (
                <Button
                  className="comment-footer-btn-reply"
                  onClick={toggleReply}
                  color="secondary"
                >
                  Responder
                </Button>
              ) : null}
              {responses && responses.length > 0 ? (
                <Button
                  className={`comment-footer-btn-responses ${
                    responses.show ? 'active' : ''
                  }`}
                  onClick={toggleResponses}
                  color="secondary"
                >
                  {`${responses.show ? 'Ocultar' : 'Mostrar'} respostas (${
                    responses.length
                  })`}
                </Button>
              ) : null}
            </Box>
            {status === 'PENDING' ? (
              <Box className="comment-footer-info pending">
                <Icon className="comment-footer-info-icon" fontSize="small">
                  warning
                </Icon>
                <Typography
                  className="comment-footer-info-text"
                  variant="caption"
                >
                  Aprovação pendente
                </Typography>
              </Box>
            ) : null}
            {status === 'REJECTED' ? (
              <Box className="comment-footer-info rejected">
                <Icon className="comment-footer-info-icon" fontSize="small">
                  error
                </Icon>
                <Typography
                  className="comment-footer-info-text"
                  variant="caption"
                >
                  Comentário rejeitado
                </Typography>
              </Box>
            ) : null}
            {!isSmallScreen && formatedDate ? (
              <Typography className="comment-footer-date" variant="caption">
                {formatedDate}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default memo(Comment);
